/**
 * FE arming + event subscription for the self-healing stop / online-backup
 * recovery path (APP-8723 Phase 3).
 *
 * Mirrors the offline-recording probe convention: Rust boots with no reconcile
 * target and performs zero file/network work until this hook arms it after the
 * kill-switch flag resolves. Flag OFF ⇒ disarm ⇒ fully inert (FR-014/FR-027).
 *
 * Also listens for `online_backup:recovery_update` and drives the existing
 * upload-required affordance (`requireBackupUploadModalAtom`) — no new UI
 * (FR-030). Watchdog / terminal-outcome note-gen gating lands in later phases.
 */

import {
  observeNativeBackupUploadSettled,
  setNativeBackupSettleChannelArmed,
} from '@/atoms/nativeBackupUploadGate';
import {
  backupUploadPromptAtom,
  nativeBackupUploadGateAtom,
  type BackupUploadPrompt,
} from '@/atoms/sessionAtoms';
import {
  getSelfHealingStopPayload,
  SELF_HEALING_STOP_FLAG_KEY,
  useIsSelfHealingStopEnabled,
} from '@/features/flags/self-healing-stop-flag';
import {
  recoveryRowProgress,
  releaseCloseGuardAfterRecovery,
  stopClassificationProgress,
  stopReasonReleasesHere,
  type ReleaseOrigin,
} from '@/hooks/mutation/recoveryCloseGuardRelease';
import {
  recordTerminalOutcome,
  type NativeTerminalOutcome,
} from '@/hooks/mutation/terminalOutcomeRelay';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { recorderFmc } from '@/tauri/controller/recording';
import { isTauri } from '@/tauri/utils';
import type { BackupRecoveryRow, StopReason } from '@/types/self-healing-stop';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { managedListen, type ManagedUnlistenFn } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { useObjectFlagValue } from '@openfeature/react-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

const RECOVERY_UPDATE_EVENT = 'online_backup:recovery_update';
const TERMINAL_OUTCOME_EVENT = 'self_healing:terminal_outcome';

/**
 * What a recovery-update should do to the upload-required affordance.
 *
 * A `switch` with a `never` fallthrough on purpose. The previous version was a
 * three-way `||` over a five-member union, so `uploaded` and `terminalEvicted`
 * were silently dropped and the modal — once opened — had no way to close.
 * TypeScript could not help, because a non-exhaustive `===` chain is not an
 * error. Adding a status to `BackupRecoveryRow` now fails to compile until it
 * is classified here.
 */
type UploadRequiredAction = 'surface' | 'clear' | 'ignore';

function liveUpdateAction(row: BackupRecoveryRow): UploadRequiredAction {
  switch (row.status) {
    // Rust owns a `pending`/`uploading` row: a failed attempt is retried in
    // process and the next launch re-attempts the rest. The note is never
    // gated on the backup (FR-034), so there is nothing to ask of the user.
    case 'pending':
    case 'uploading':
      return 'ignore';
    // Retries are exhausted or the TTL expired. The audio is kept but nothing
    // will retry it on its own, so this is the genuinely user-actionable state
    // FR-035 means by "surfaced" — the modal offers a native retry for it.
    case 'abandonedRetained':
      return 'surface';
    // Terminal-happy: the backup landed, or the server said the target is gone
    // and the ciphertext was securely deleted. Either way nothing is pending,
    // so anything we raised earlier must come down. A stop retains the
    // encrypted backup *before* uploading, so the durable row is born
    // `pending`, and with nothing to lower it a "do not refresh" prompt stayed
    // on screen after a completely healthy stop.
    case 'uploaded':
    case 'terminalEvicted':
      return 'clear';
    default: {
      const exhaustive: never = row.status;
      return exhaustive;
    }
  }
}

/**
 * Whether this row says the NATIVE backup upload has stopped running.
 *
 * Separate from {@link liveUpdateAction} because it answers a different
 * question: that one decides what to do with the *prompt*, this one settles the
 * `nativeBackupUploadGateAtom` watch — the fact the upload-required modal reads
 * to keep its recovery actions hidden while a detached upload is live. An
 * `abandonedRetained` row is a settle for this purpose even though it raises a
 * prompt: nothing is uploading any more, so the exit must come back.
 *
 * Exhaustive by the same `never` fallthrough, so a new status has to be
 * classified here too rather than defaulting to "still uploading" and leaving
 * the modal raised forever.
 */
function isNativeUploadSettled(row: BackupRecoveryRow): boolean {
  switch (row.status) {
    case 'pending':
    case 'uploading':
      return false;
    case 'abandonedRetained':
    case 'uploaded':
    case 'terminalEvicted':
      return true;
    default: {
      const exhaustive: never = row.status;
      return exhaustive;
    }
  }
}

/**
 * Whether a row found *at mount* should raise the affordance. Same answer as
 * a live update: a `pending`/`uploading` row that survived a restart is picked
 * up by the arm-time reconcile that runs alongside this read, so only a row
 * that has already given up is the clinician's to act on.
 */
function shouldSurfaceOnHydrate(row: BackupRecoveryRow): boolean {
  return row.status === 'abandonedRetained';
}

/**
 * Whether a terminal-happy row takes down the prompt currently on screen.
 *
 * Rows are keyed `(sessionId, recordingId)`, so a native prompt for R1 must
 * survive R2 of the same session landing — only its own row may clear it. An
 * FE-upload prompt carries no recording id; it keeps the session-scoped clear
 * it always had (a healthy stop's `uploaded` lowers a "do not
 * refresh" prompt left over for that session).
 */
function isSettledBy(current: BackupUploadPrompt, row: BackupRecoveryRow): boolean {
  if (!current || current.sessionId !== row.sessionId) return false;
  if (isClinicianOwnedKind(current)) return false;
  if (current.kind === 'frontendUpload') return true;
  return current.recordingId === row.recordingId;
}

/**
 * Whether the prompt on screen is the CLINICIAN's to act on for this session.
 *
 * `captureTruncated` and `silentCapture` both ask the user to make a decision
 * — keep a short recording, pick a different mic — and a native backup row has
 * no standing to answer it for them. That is why a settled row may not DISMISS
 * them (`isSettledBy`), and it is just as true of the surface direction: an
 * `abandonedRetained` row used to `setUploadPrompt` unconditionally, which
 * replaced whatever was on screen — including the mic picker this feature
 * exists to show. Hydrate already refused to overwrite a live prompt; the live
 * event stream did not.
 *
 * Session-scoped, exactly as `isSettledBy` is: a prompt belonging to a
 * DIFFERENT session is stale, and a native row for the session actually
 * recording still wins.
 */
function isClinicianOwned(current: BackupUploadPrompt, row: BackupRecoveryRow): boolean {
  if (!current || current.sessionId !== row.sessionId) return false;
  return isClinicianOwnedKind(current);
}

/**
 * The kind half of the rule, as a type predicate so the callers above keep
 * their narrowing (`isSettledBy` reads `current.recordingId` once these two
 * kinds are excluded). Session scoping is the caller's, deliberately: a
 * negative answer here means "not one of these kinds", never "wrong session".
 */
function isClinicianOwnedKind(
  current: NonNullable<BackupUploadPrompt>
): current is Extract<
  NonNullable<BackupUploadPrompt>,
  { kind: 'captureTruncated' | 'silentCapture' }
> {
  return current.kind === 'captureTruncated' || current.kind === 'silentCapture';
}

export function useSelfHealingStopEvents(): void {
  const enabled = useIsSelfHealingStopEnabled();
  const { isAuthenticated } = useIsAuthenticated();
  const queryClient = useQueryClient();
  const setUploadPrompt = useSetAtom(backupUploadPromptAtom);
  const setNativeBackupUploadGate = useSetAtom(nativeBackupUploadGateAtom);
  const armChainRef = useRef(Promise.resolve());
  // Re-arm when any payload field changes — not only the boolean flip — so
  // Rust thresholds (incl. `feLeaseGraceSecs`) stay in sync with the live FE
  // lease heartbeat interval derived from the same payload.
  const flagPayload = useObjectFlagValue(SELF_HEALING_STOP_FLAG_KEY, {});
  const payloadSyncKey = enabled ? JSON.stringify(flagPayload) : '';

  // Arm / disarm the Rust reconcile once the flag is known and the user is
  // authenticated (data-model.md §6b). No-op on web. Serialized so a fast
  // flag/auth flip cannot leave reconcile armed after disarm.
  useEffect(() => {
    if (!isTauri()) return;

    let cancelled = false;
    const shouldArm = enabled && isAuthenticated;
    // DSK-879: the upload-required modal's native gate is only allowed to be up
    // while THIS is armed, and the write is made here — synchronously, off the
    // same `shouldArm`, in the same effect, on the same
    // `[enabled, isAuthenticated]` key — rather than anywhere downstream, so the
    // FE's belief about the producer cannot drift from what the producer was
    // told. Disarming drops every watch: after `store.set_armed(false)` no
    // reconcile pass starts, no in-process retry is scheduled, and a pending
    // retry timer bails on its own `is_armed()` re-check, so nothing is even
    // attempting the work that would move a row to a settled status. The settle
    // is not merely unheard, it is unsent — a gate left up here could only be up
    // forever.
    setNativeBackupUploadGate((gate) =>
      setNativeBackupSettleChannelArmed(gate, shouldArm)
    );

    armChainRef.current = armChainRef.current
      .then(async () => {
        if (cancelled) return;
        // Arming also carries the flag's payload down to Rust, which decodes
        // it into the self-healing thresholds (incl. `asyncHealthyBackup`) —
        // this is the production writer of that config (T067 rollout path).
        await recorderFmc.onlineBackupArmReconcile(
          shouldArm
            ? { enabled: true, payload: getSelfHealingStopPayload() }
            : { enabled: false }
        );
      })
      .catch((err) => {
        if (cancelled) return;
        // A classification, never the caught error. The commands and
        // listeners behind every rejection in this file carry backup file
        // paths and session ids, and `logger` forwards to Datadog, which
        // redacts nothing. `errorKind` is the classification; `sanitizeError`
        // is the projection beside it, and on its own it would say nothing at
        // all here — none of these paths can produce an axios error, and it
        // projects everything else to `{}`.
        logger.warn('[useSelfHealingStopEvents] arm reconcile failed', {
          errorKind: bridgeErrorKind(err),
          error: sanitizeError(err),
        });
      });

    return () => {
      cancelled = true;
      // Mirrors the disarm below, synchronously: the cleanup is what runs on a
      // flag flip, an unmount and a sign-out, and the gate must be down before
      // the IPC that makes it unclearable is even sent.
      setNativeBackupUploadGate((gate) => setNativeBackupSettleChannelArmed(gate, false));
      armChainRef.current = armChainRef.current
        .then(async () => {
          await recorderFmc.onlineBackupArmReconcile({ enabled: false });
        })
        .catch(() => {
          /* best-effort disarm */
        });
    };
  }, [enabled, isAuthenticated, payloadSyncKey, setNativeBackupUploadGate]);

  // Drive the existing upload-required modal from durable recovery updates.
  useEffect(() => {
    if (!isTauri() || !enabled) return;

    let unlisten: ManagedUnlistenFn | undefined;
    let cancelled = false;

    /**
     * Release the app-quit close guard for a recovery that has finished.
     *
     * A thin forwarder on purpose: the per-session, per-epoch liveness gate
     * lives inside {@link releaseCloseGuardAfterRecovery}, with the write, so
     * this listener cannot release without it and a future releasing site here
     * inherits it without its author remembering. All this adds is what only
     * the listener knows — the recording id the relay is keyed by, the epoch's
     * reason when a native stop handed it to us, and whether the effect that
     * owns this write has since been torn down.
     *
     * `consultationIntent` is `unstated` at both native sites, and that is the
     * honest answer: they observe a recovery FINISHING, which says a backup
     * settled and says nothing about whether the consultation is over. The gate
     * decides that on its own evidence.
     */
    const releaseAfterRecovery = (
      target: { sessionId: string; recordingId: string; epochEndReason?: StopReason },
      origin: ReleaseOrigin
    ) =>
      releaseCloseGuardAfterRecovery({
        sessionId: target.sessionId,
        recordingId: target.recordingId,
        epochEndReason: target.epochEndReason,
        consultationIntent: 'unstated',
        queryClient,
        origin,
        isCancelled: () => cancelled,
      });

    /**
     * Release for a stop the FE never initiated — a shutdown or a watchdog
     * termination. Nothing FE-side writes `recording_status` for those today,
     * which is the hole this closes.
     *
     * THE SESSION ID IS NOT IN THE EVENT. The payload carries a recording id
     * and a generation, so the session id is taken from the durable recovery
     * row that OWNS that recording — never from the newest cached session or
     * the current route, either of which would silently release somebody
     * else's live consultation. No owning row means no id, and no id means log
     * and do not call.
     *
     * The owning row is also re-read for its own status: a `finalized` stop
     * whose backup is still `pending` has a recovery in flight, and in flight
     * does not release.
     *
     * For the `shutdown` reason specifically, expect this PATCH often not to
     * land: the webview is being torn down around it, and an in-flight request
     * dies with the process. That is why the durable recovery row is the
     * dependable releaser for a shutdown and this branch is the opportunistic
     * one — it is what acts when the quit is cancelled or slow, and it costs
     * nothing when it is not.
     */
    const releaseForNativeStop = async (outcome: NativeTerminalOutcome) => {
      if (!stopReasonReleasesHere(outcome.reason)) return;
      if (stopClassificationProgress(outcome.classification) !== 'finished') return;

      let rows: BackupRecoveryRow[] | undefined;
      try {
        rows = await recorderFmc.onlineBackupRecoveryStatusQuietly();
      } catch (err) {
        // A classification, never the caught error: a recovery-row read
        // failure can surface native paths to retained backup audio. Which is
        // why `errorKind` is here — this is one of the lines whose whole job is
        // to say WHY the guard did not come down, and the sanitised projection
        // is empty for a bridge rejection.
        logger.warn('[close-guard] recovery rows unreadable; not releasing', {
          recordingId: outcome.recordingId,
          reason: outcome.reason,
          errorKind: bridgeErrorKind(err),
          error: sanitizeError(err),
        });
        return;
      }
      if (cancelled) return;

      const owning = rows?.find((row) => row.recordingId === outcome.recordingId);
      if (!owning) {
        logger.warn('[close-guard] no recovery row owns this recording; not releasing', {
          recordingId: outcome.recordingId,
          reason: outcome.reason,
        });
        return;
      }
      if (recoveryRowProgress(owning.status) !== 'finished') return;
      // The reason is in hand here, so the relay is not consulted: this is the
      // one releasing site that observes the epoch's ending directly.
      await releaseAfterRecovery(
        {
          sessionId: owning.sessionId,
          recordingId: outcome.recordingId,
          epochEndReason: outcome.reason,
        },
        'nativeStop'
      );
    };

    // FR-021a (T044): feed every typed terminal outcome into the relay, so a
    // stop can be resolved from what the native routine reported rather than
    // from the FE's own timeout — and so a stop the FE never initiated
    // (watchdog, shutdown, another window) is observable at all. Emitted
    // unconditionally by `terminal::execute`; nothing consumed it before this.
    let unlistenOutcome: ManagedUnlistenFn | undefined;
    void managedListen<NativeTerminalOutcome>(TERMINAL_OUTCOME_EVENT, (event) => {
      if (cancelled) return;
      if (!event.payload) return;
      recordTerminalOutcome(event.payload);
      void releaseForNativeStop(event.payload);
    })
      .then((fn) => {
        if (cancelled) {
          void fn();
          return;
        }
        unlistenOutcome = fn;
      })
      .catch((err) => {
        // `error`, not `warn`: if this never attaches, T044 silently degrades
        // to reading the FE's own timeout as an outcome — the exact behaviour
        // this listener exists to replace — and nothing else in the app would
        // report it. Deliberately not retried: the repo's retry precedent
        // (`tauri/events/audio-levels.ts`) carries its own backoff schedule and
        // teardown race handling, and duplicating that here for a listener that
        // only fails when Tauri IPC is already broken buys little.
        logger.error(
          '[useSelfHealingStopEvents] listen terminal_outcome failed; stop completion falls back to the FE timeout',
          { errorKind: bridgeErrorKind(err), error: sanitizeError(err) }
        );
      });

    void managedListen<BackupRecoveryRow>(RECOVERY_UPDATE_EVENT, (event) => {
      if (cancelled) return;
      const row = event.payload;
      if (!row) return;
      // Independent of the prompt decision below: the stop opened a watch for
      // this session before its own stop IPC, and the durable row is the only
      // thing that ever reports that detached upload settling. Settling a watch
      // that is still `watching` is what makes this safe to receive BEFORE the
      // raise as well as after it — the raise still on its way is then refused
      // instead of latching a gate nothing will lower.
      if (isNativeUploadSettled(row)) {
        setNativeBackupUploadGate((gate) =>
          observeNativeBackupUploadSettled(gate, row.sessionId)
        );
      }
      // Separate from the settle above, and the row is the only place the
      // session id for this recovery exists. A settled row is not the same
      // question as a finished recovery: `abandonedRetained` settles the gate
      // (nothing is uploading) yet still offers the clinician a retry, so the
      // recovery is not over and the guard stays armed.
      if (recoveryRowProgress(row.status) === 'finished') {
        // No reason in hand — a row records that a backup finished, never why
        // its recording stopped — so the gate asks the relay for this
        // recording's own ending before it releases anything.
        void releaseAfterRecovery(
          { sessionId: row.sessionId, recordingId: row.recordingId },
          'recoveryRow'
        );
      }
      const action = liveUpdateAction(row);
      if (action === 'surface') {
        // Functional update, not a bare set: the live surface must read what
        // is on screen before replacing it, the same way hydrate does.
        setUploadPrompt((current) =>
          isClinicianOwned(current, row)
            ? current
            : {
                kind: 'nativeAbandoned',
                sessionId: row.sessionId,
                recordingId: row.recordingId,
              }
        );
      } else if (action === 'clear') {
        setUploadPrompt((current) => (isSettledBy(current, row) ? null : current));
      }
    })
      .then((fn) => {
        if (cancelled) {
          void fn();
          return;
        }
        unlisten = fn;
      })
      .catch((err) => {
        // Same reasoning: without this the upload-required affordance never
        // updates for the rest of the session.
        logger.error(
          '[useSelfHealingStopEvents] listen recovery_update failed; the upload-required affordance will not update',
          { errorKind: bridgeErrorKind(err), error: sanitizeError(err) }
        );
      });

    // Hydrate from the durable store on mount so a restart with a given-up row
    // still surfaces the existing affordance without waiting for a new event.
    void recorderFmc
      .onlineBackupRecoveryStatusQuietly()
      .then((rows) => {
        if (cancelled || !rows) return;
        const abandonedRow = rows.find(shouldSurfaceOnHydrate);
        if (abandonedRow) {
          // Only populate a cold mount: any live prompt — an in-flight
          // `frontendUpload`, or a native one for another recording of the
          // same session — must not be replaced by a hydrate. A flag flip
          // re-runs this effect mid-session, and the live event stream owns
          // updates from there.
          setUploadPrompt((current) =>
            current === null
              ? {
                  kind: 'nativeAbandoned',
                  sessionId: abandonedRow.sessionId,
                  recordingId: abandonedRow.recordingId,
                }
              : current
          );
        }
      })
      .catch((err) => {
        // The same command as the native-stop path's catch, and the same
        // reason: a recovery-row read failure can surface native paths to
        // retained backup audio. Dropping the toast left this log as its only
        // sink, so it is the classified-and-sanitised one.
        logger.warn('[useSelfHealingStopEvents] recovery status read failed', {
          errorKind: bridgeErrorKind(err),
          error: sanitizeError(err),
        });
      });

    return () => {
      cancelled = true;
      void unlisten?.();
      void unlistenOutcome?.();
    };
  }, [enabled, setUploadPrompt, setNativeBackupUploadGate, queryClient]);
}
